import classNames from 'classnames';
import React from 'react';
import CtaButton from '../../sharedComponents/CtaButton';
import LazyloadImage from '../../sharedComponents/LazyloadImage';
import { CtaSigninSection, SIGNUP_URL } from '../utils';
import './callToActionHero.scss';

const ctaHeaderClasses = classNames(
    'eds-bg-color--white',
    'eds-l-pad-all-6',
    'eds-l-sm-pad-all-6',
    'eds-l-md-pad-all-8',
    'eds-l-lg-pad-all-12',
    'eds-l-lw-pad-all-12',
    'eds-l-sw-mar-top-6',
    'eds-l-mn-mar-top-6',
    'eds-l-md-mar-top-8',
    'eds-l-mw-mar-top-20',
    'eds-l-ln-mar-top-20',
    'eds-l-lg-mar-top-20',
    'eds-l-lw-mar-top-20',
    'eds-sn-w-100p',
    'eds-sm-w-100p',
    'mktg-shadow-primary',
    'marketing--form',
);

const ctaHeadlineClasses = classNames(
    'mktg-sign-up-form-h1',
    'eds-text-color--grey-900',
    'eds-l-pad-vert-4',
);
const ctaSubheaderClasses = classNames(
    'eds-text-bl',
    'eds-text-weight--regular',
    'eds-text-color--grey-700',
    'eds-l-pad-bot-4',
    'eds-align--center',
    'eds-mn-min-h-13',
    'eds-md-min-h-34',
    'eds-mw-min-h-34',
    'eds-ln-min-h-37',
    'eds-lg-min-h-37',
    'eds-lw-min-h-37',
);
const ctaGridClasses = classNames('eds-g-cell-12-12', 'eds-g-offset-0-12');
const ctaHeaderContainerClasses =
    ctaGridClasses + ' eds-g-cell-md-5-12 eds-g-cell--has-overflow';
const ctaImageClasses =
    ctaGridClasses + ' eds-g-cell-md-8-12 eds-l-pad-top-14 eds-l-sm-pad-top-6';
const centerAlignedHeaderClasses =
    ctaGridClasses +
    ' eds-g-cell eds-g-cell-md-10-12 eds-g-offset-md-1-12 mktg-form-bg';
const signupGridClasses = classNames(
    'eds-g-cell',
    'eds-g-cell-1-1',
    'eds-l-mar-top-4',
);

interface MktgClassMapInterface {
    right: string;
    left: string;
}
const getMktgClass = (formAlignment: string) => {
    const mktgClassMap = {
        right: 'left',
        left: 'right',
    };
    return mktgClassMap[formAlignment as keyof MktgClassMapInterface];
};

interface CenterAlignedCtaHeroModuleProps {
    headline: string;
    subheader: string;
    sideImage: string;
    buttonText: string;
    buttonUrl?: string;
    moduleClass: string;
}
export const CenterAlignedCtaHeroModule = ({
    headline,
    subheader,
    sideImage,
    buttonText,
    buttonUrl,
    moduleClass,
}: CenterAlignedCtaHeroModuleProps) => (
    <div className={moduleClass}>
        <section className="marketing-container__max-width_hero">
            <div
                className="mktg-landing-hero-cover"
                style={{ backgroundImage: `url(${sideImage})`, height: '82vh' }}
            ></div>
        </section>
        <section className="eds-bg-color--grey-100 eds-l-pad-hor-3">
            <div className="mktg-grid-container">
                <div className={centerAlignedHeaderClasses}>
                    <div className="mktg-form-inner">
                        <div className="eds-l-pad-bot-2">
                            <h1
                                className="eds-text-hm eds-text-color--grey-800 eds-text--center"
                                style={{ verticalAlign: 'inherit' }}
                            >
                                {headline}
                            </h1>
                        </div>
                        <div className="eds-l-pad-hor-2 eds-l-mar-bot-2">
                            <h2 className="eds-text-bl eds-text--center">
                                <span className="eds-show-up-sw">
                                    {subheader}
                                </span>
                            </h2>
                        </div>
                        <CtaButton
                            buttonText={buttonText}
                            buttonUrl={buttonUrl || SIGNUP_URL}
                            buttonClasses={'eds-align--center'}
                        />
                    </div>
                </div>
            </div>
        </section>
    </div>
);

interface CallToActionHeroModuleProps {
    headline: string;
    subheader: string;
    shouldIncludeSigninFields: boolean;
    buttonText: string;
    buttonUrl?: string;
    sideImage: string;
    formAlignment: string;
    backgroundImage: string;
    moduleClass: string;
    alt?: string;
}
const CallToActionHeroModule = ({
    headline,
    subheader,
    shouldIncludeSigninFields,
    buttonText,
    buttonUrl,
    sideImage,
    formAlignment,
    backgroundImage,
    moduleClass,
    alt,
}: CallToActionHeroModuleProps) => {
    const centerAlignedProps = {
        headline,
        subheader,
        sideImage,
        buttonText,
        buttonUrl,
        moduleClass,
    };

    if (formAlignment !== 'center') {
        return (
            <section
                className={`marketing-container__max-width_hero ${moduleClass}`}
            >
                <div
                    className="mktg-landing-hero-cover"
                    style={{ backgroundImage: `url(${backgroundImage})` }}
                >
                    <div className="marketing-container__max-width">
                        <div className="eds-g-grid eds-l-pad-hor-6">
                            <div
                                className={`mktg-device-${getMktgClass(
                                    formAlignment,
                                )}-align`}
                            >
                                <div className={ctaImageClasses}>
                                    <div className="l-align-left">
                                        <LazyloadImage
                                            image={sideImage}
                                            alt={alt || headline}
                                            classes={
                                                'eds-max-img marketing--hero-img'
                                            }
                                            style={{}}
                                        />
                                    </div>
                                </div>
                                <div className={ctaHeaderContainerClasses}>
                                    <div className="eds-align--center-vertical">
                                        <div
                                            className={ctaHeaderClasses}
                                            style={{
                                                padding: '10px 48px 48px 48px',
                                            }}
                                        >
                                            <h1 className={ctaHeadlineClasses}>
                                                {headline}
                                            </h1>
                                            <h3 className={ctaSubheaderClasses}>
                                                {subheader}
                                            </h3>
                                            <div className="eds-g-grid">
                                                <CtaSigninSection
                                                    shouldIncludeSigninFields={
                                                        shouldIncludeSigninFields
                                                    }
                                                    buttonText={buttonText}
                                                    buttonUrl={
                                                        buttonUrl || SIGNUP_URL
                                                    }
                                                    signupGridClasses={
                                                        signupGridClasses
                                                    }
                                                    showMktgFormClass={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
    return <CenterAlignedCtaHeroModule {...centerAlignedProps} />;
};
export default CallToActionHeroModule;
